import { loading, success, failure } from 'utilities/actions'
import { put, take, takeLatest, takeEvery, all, fork } from 'redux-saga/effects'

import * as actions from './actions'
import { sortParticipants } from './selectors'

import { API_URL, PARTICIPANTS_ENDPOINT, PARTICIPANTS_PAGE_SIZE } from 'qap/constants'
import { requestActions } from 'state/request'
import { stringify } from 'utilities/apiQueryString'
import { decamelizeKeys } from 'utilities/humps'

// The participants list has no server-side sort or filters and is intentionally
// paginated invisibly: we keep fetching PARTICIPANTS_PAGE_SIZE records at a time
// until a short page signals the end, accumulate them all, then apply the AC
// ordering client-side.
//
// Each page goes through AUTHED_REQUEST (rather than calling fetch directly) so
// 401/token-invalid responses are routed to expireSession and failures get the
// app's global error handling. AUTHED_REQUEST is fire-and-forget, so we await
// each page's resolution with `take` before requesting the next.
export function* fetchParticipants(): Generator<any, any, any> {
  yield put({ type: loading(actions.API_PARTICIPANTS_INDEX) })

  let offset = 0
  let all: any[] = []

  while (true) {
    const queryString = stringify({ page: { offset, limit: PARTICIPANTS_PAGE_SIZE } })
    const url = `${API_URL}/${PARTICIPANTS_ENDPOINT}?${queryString}`

    yield put({
      type: requestActions.AUTHED_REQUEST,
      method: 'GET',
      url,
      cancelKey: 'participants.index',
      hideLoading: true,
      successAction: success(actions.API_PARTICIPANTS_PAGE),
      failureAction: failure(actions.API_PARTICIPANTS_PAGE),
    })

    // Wait for this page to resolve. A token-invalid 401 never resolves here:
    // the request middleware dispatches expireSession instead, logging the user out.
    const response = yield take([
      success(actions.API_PARTICIPANTS_PAGE),
      failure(actions.API_PARTICIPANTS_PAGE),
    ])

    if (response.type === failure(actions.API_PARTICIPANTS_PAGE)) {
      yield put({
        type: failure(actions.API_PARTICIPANTS_INDEX),
        errors: response.errors?.length ? response.errors : ['Failed to load'],
      })
      return
    }

    const pageData = response.data || []
    all = all.concat(pageData)

    if (pageData.length < PARTICIPANTS_PAGE_SIZE) break
    offset += PARTICIPANTS_PAGE_SIZE
  }

  yield put({
    type: success(actions.API_PARTICIPANTS_INDEX),
    data: sortParticipants(all),
    total: all.length,
  })
}

// PATCH a single participant. The API accepts a partial `{ data: { ... } }` body
// (snake_cased) and returns the updated record, which the reducer folds back into
// the list by id. `context` carries the id through to the success/failure action.
export function* updateParticipant(action: any): Generator<any, any, any> {
  const { id, changes } = action

  yield put({
    type: requestActions.AUTHED_REQUEST,
    method: 'PATCH',
    url: `${API_URL}/${PARTICIPANTS_ENDPOINT}/${id}`,
    body: { data: decamelizeKeys(changes) },
    cancelKey: 'participants.update',
    successAction: success(actions.API_PARTICIPANTS_UPDATE),
    failureAction: failure(actions.API_PARTICIPANTS_UPDATE),
    context: { id },
  })
}

export function* createParticipant(action: any): Generator<any, any, any> {
  const { participant } = action

  // No cancelKey: this POST is not idempotent, so it must not be aborted by a
  // later request (an aborted create exits without a failure action and may
  // still have been processed server-side).
  yield put({
    type: requestActions.AUTHED_REQUEST,
    method: 'POST',
    url: `${API_URL}/${PARTICIPANTS_ENDPOINT}`,
    body: { data: decamelizeKeys(participant) },
    successAction: success(actions.API_PARTICIPANTS_CREATE),
    failureAction: failure(actions.API_PARTICIPANTS_CREATE),
  })
}

// POST to the archive/unarchive endpoints. These carry no cancelKey: a fixed key
// would let one row's action abort another's in-flight request, and an aborted
// request exits without a failure action, leaving that row stuck as archiving.
// The endpoints are idempotent, so no retry-safety concern remains. The API
// returns the updated participant (with its new `active` flag), which the reducer
// folds back in and re-sorts. `context` carries the id to the success/failure.
export function* archiveParticipant(action: any): Generator<any, any, any> {
  const { id } = action

  yield put({
    type: requestActions.AUTHED_REQUEST,
    method: 'POST',
    url: `${API_URL}/${PARTICIPANTS_ENDPOINT}/${id}/archive`,
    successAction: success(actions.API_PARTICIPANTS_ARCHIVE),
    failureAction: failure(actions.API_PARTICIPANTS_ARCHIVE),
    context: { id },
  })
}

export function* unarchiveParticipant(action: any): Generator<any, any, any> {
  const { id } = action

  yield put({
    type: requestActions.AUTHED_REQUEST,
    method: 'POST',
    url: `${API_URL}/${PARTICIPANTS_ENDPOINT}/${id}/unarchive`,
    successAction: success(actions.API_PARTICIPANTS_UNARCHIVE),
    failureAction: failure(actions.API_PARTICIPANTS_UNARCHIVE),
    context: { id },
  })
}

function* watchFetch(): Generator<any, any, any> {
  yield takeLatest(actions.API_PARTICIPANTS_INDEX, fetchParticipants)
}

function* watchUpdate(): Generator<any, any, any> {
  yield takeEvery(actions.API_PARTICIPANTS_UPDATE, updateParticipant)
}

function* watchCreate(): Generator<any, any, any> {
  yield takeEvery(actions.API_PARTICIPANTS_CREATE, createParticipant)
}

function* watchArchive(): Generator<any, any, any> {
  yield takeEvery(actions.API_PARTICIPANTS_ARCHIVE, archiveParticipant)
}

function* watchUnarchive(): Generator<any, any, any> {
  yield takeEvery(actions.API_PARTICIPANTS_UNARCHIVE, unarchiveParticipant)
}

export default function* participantsSagas(): Generator<any, any, any> {
  yield all([
    fork(watchFetch),
    fork(watchUpdate),
    fork(watchCreate),
    fork(watchArchive),
    fork(watchUnarchive)
  ])
}
